import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../../contexts/auth';
import styles from './tenantBasicInfo.module.css';

type DetailRowProps = {
  label: string;
  value: React.ReactNode;
  noBorder?: boolean;
};

const DetailRow = ({ label, value, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' },
      gap: { xs: 0.5, sm: 2 },
      alignItems: 'start',
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? 'none' : '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: '24px', display: 'flex', alignItems: 'center' }}>{value}</Box>
  </Box>
);

const formatDate = (value?: string) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

export const TenantBasicInfo = () => {
  const { t } = useTranslation();
  const { currentUser, currentUserLoading } = useAuthContext();
  const tenant = currentUser?.tenant;

  if (currentUserLoading || !tenant) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.section}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <DetailRow
            label={t('State')}
            value={<Typography variant="subtitle1">{tenant.is_active ? t('Active') : t('Inactive')}</Typography>}
            noBorder={tenant.is_active}
          />
          {!tenant.is_active && (
            <DetailRow
              label={t('Scheduled termination date')}
              value={<Typography variant="subtitle1">{tenant.termination_date ? formatDate(tenant.termination_date) : t('Not scheduled')}</Typography>}
              noBorder
            />
          )}
        </Box>
      </Box>
      <Divider />
      <Box className={styles.section}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <DetailRow label={t('Name')} value={<Typography variant="subtitle1">{tenant.name || ''}</Typography>} />
          <DetailRow label={t('Website')} value={<Typography variant="subtitle1">{tenant.website || ''}</Typography>} />
          <DetailRow label={t('Logo')} value={<Typography variant="subtitle1">{tenant.logo || ''}</Typography>} noBorder />
        </Box>
      </Box>
      <Divider />
      <Box className={styles.section}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <DetailRow label={t('NIP')} value={<Typography variant="subtitle1">{tenant.nip || ''}</Typography>} />
          <DetailRow label={t('REGON')} value={<Typography variant="subtitle1">{tenant.regon || ''}</Typography>} />
          <DetailRow label={t('KRS')} value={<Typography variant="subtitle1">{tenant.krs || ''}</Typography>} />
          <DetailRow label={t('PESEL')} value={<Typography variant="subtitle1">{tenant.pesel || ''}</Typography>} noBorder />
        </Box>
      </Box>
    </Box>
  );
};
